import React from 'react';
import _ from 'lodash';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  useOnboardingProfileActivityQuery,
  useSaveOnboardingProfileMutation
} from 'generated/graphql';
import {
  enumCache,
  fileData2EncodedString,
  useLoading,
  validationUtil,
  imageUtil
} from 'modules';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';



export type CanLeadInvestmentsOptionNames = 'yes' | 'no';

const __formSchema = yup.object({
  profilePictureFile: validationUtil.fileData.nullable().default(undefined),
  companyName: yup.string().required('Company name is required.'),
  websiteUrl: validationUtil.websiteUrlSchema,
  phoneNumber: yup.string(),
  companyDescription: yup.string().max(250),
  investmentCategory: yup.string().nullable(),
  investmentStages: yup.array().of(validationUtil.investmentStageSchema.required()),
  investmentGeographies: yup.array().of(yup.string()),
  investmentAreas: yup.array().of(yup.string().uuid().required()),
  checkSizeMinDollars: validationUtil.positiveIntegerSchema.nullable(),
  checkSizeMaxDollars: validationUtil.positiveIntegerSchema.nullable().min(yup.ref('checkSizeMinDollars'), 'Must be no less than minimum value'),
  canLeadInvestments: yup.mixed<CanLeadInvestmentsOptionNames>(),
  city: validationUtil.nonEmptyStringSchema.required('City name is required.'),
  usState: yup.string(),
  country: yup.string().required('Country is required.')
}).required();

export type IFormData = yup.InferType<typeof __formSchema>;



export function useProfileActivity() {
  const tuple = useOnboardingProfileActivityQuery({ fetchPolicy: 'no-cache' });
  const isInvestor = tuple.data?.loggedInUser?.type?.name === 'investor';
  const profilePictureUrl = tuple.data?.loggedInUser?.companyProfile?.logo?.key
    ? imageUtil.getFileUrl(tuple.data?.loggedInUser.companyProfile?.logo?.key)
    : undefined;

  const [saveSiteUser, saveSiteUserTuple] = __useSaveOnboardingProfile({
    isInvestor,
    companyId: tuple.data?.loggedInUser?.companyProfile?.id ?? '',
    investorId: tuple.data?.loggedInUser?.investorProfile?.id ?? ''
  });
  const loading = useLoading([tuple, saveSiteUserTuple]);

  const form = useForm<IFormData>({
    resolver: yupResolver(__formSchema),
    shouldFocusError: true
  });



  React.useEffect(() => {
    if (!loading && tuple.data) {
      form.setValue('companyName', tuple.data.loggedInUser?.companyProfile?.name ?? '');
      form.setValue('companyDescription', tuple.data.loggedInUser?.companyProfile?.description);
      form.setValue('websiteUrl', tuple.data.loggedInUser?.companyProfile?.websiteUrl);
      form.setValue('phoneNumber', tuple.data.loggedInUser?.companyProfile?.phoneNumber);
      form.setValue('investmentCategory', tuple.data.loggedInUser?.companyProfile?.investmentCategory?.name);
      form.setValue('investmentGeographies', tuple.data.loggedInUser?.companyProfile?.investmentGeographies.map(g => g.name));
      form.setValue('investmentAreas', tuple.data.loggedInUser?.companyProfile?.investmentAreas.map(a => a.id));
      form.setValue('investmentStages', tuple.data.loggedInUser?.companyProfile?.investmentStages?.map(s => s.name));
      form.setValue('checkSizeMinDollars', tuple.data.loggedInUser?.investorProfile?.checkSizeMinDollars);
      form.setValue('checkSizeMaxDollars', tuple.data.loggedInUser?.investorProfile?.checkSizeMaxDollars);
      form.setValue('canLeadInvestments', tuple.data.loggedInUser?.investorProfile?.canLeadInvestments === false ? 'no' : 'yes');
      form.setValue('country', tuple.data.loggedInUser?.companyProfile?.address.country ?? 'US');
      form.setValue('city', tuple.data.loggedInUser?.companyProfile?.address.city ?? '');
      form.setValue('usState', tuple.data.loggedInUser?.companyProfile?.address.usState ?? '');
    }
  }, [form, loading, tuple.data]);



  return {
    form,
    loading,
    data: {
      isInvestor,
      profilePictureUrl,
      hasAccessPass: !_.isNil(tuple.data?.loggedInUser?.activeAccessPass)
    },
    mutations: {
      saveSiteUser
    }
  };
}



interface ISaveSiteUserProps {
  isInvestor: boolean;
  companyId: string;
  investorId: string;
}

function __useSaveOnboardingProfile(props: ISaveSiteUserProps) {
  const [fnMutate, tuple] = useSaveOnboardingProfileMutation();

  async function fnWrappedMutate(formData: IFormData) {
    await fnMutate({
      variables: {
        companyProfile: {
          id: props.companyId,
          name: formData.companyName,
          logo: formData.profilePictureFile ? fileData2EncodedString(formData.profilePictureFile) : undefined,
          websiteUrl: formData.websiteUrl,
          phoneNumber: formData.phoneNumber,
          description: formData.companyDescription,
          investmentCategory: formData.investmentCategory as enumCache.IInvestmentCategory,
          investmentGeographies: formData.investmentGeographies as enumCache.IInvestmentGeography[],
          investmentStages: formData.investmentStages as enumCache.IInvestmentStage[],
          investmentAreas: formData.investmentAreas,
          address: {
            city: formData.city,
            usState: formData.usState,
            country: formData.country
          }
        },
        isInvestor: props.isInvestor,
        investorProfile: {
          id: props.investorId,
          checkSizeMinDollars: formData.checkSizeMinDollars,
          checkSizeMaxDollars: formData.checkSizeMaxDollars,
          canLeadInvestments: formData.canLeadInvestments === 'yes'
        }
      }
    });
  }

  return [fnWrappedMutate, tuple] as const;
}
